"use client";

import { useState } from "react";
import * as htmlToImage from "html-to-image";
import { createRoot } from "react-dom/client";
import { Button } from "@/components/ui/button";
import { Share2, Loader2 } from "lucide-react";
import { ShareCard } from "@/components/ShareCard";
import { toast } from "sonner";

interface ShareImageButtonProps {
  className?: string;
  score?: number;
  url?: string;
  createdAt?: string | Date;
  pageSpeedScore?: number;
  websiteQualityScore?: number;
  trustSecurityScore?: number;
}

export function ShareImageButton({
  className = "",
  score,
  url,
  createdAt,
  pageSpeedScore,
  websiteQualityScore,
  trustSecurityScore,
}: ShareImageButtonProps) {
  const [isGenerating, setIsGenerating] = useState(false);
  const reportIdFromUrl =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("report") ?? undefined
      : undefined;

  const createOffscreenContainer = (width: number, height: number) => {
    const host = document.createElement("div");
    host.setAttribute("aria-hidden", "true");
    host.style.position = "fixed";
    host.style.left = "-10000px";
    host.style.top = "-10000px";
    host.style.pointerEvents = "none";
    host.style.width = `${width}px`;
    host.style.height = `${height}px`;
    document.body.appendChild(host);
    return host;
  };

  const renderCardInto = (host: HTMLElement, width: number, height: number) => {
    const root = createRoot(host);
    root.render(
      <ShareCard
        width={width}
        height={height}
        score={typeof score === "number" ? score : 0}
        url={url ?? "https://example.com"}
        createdAt={createdAt ?? new Date()}
        pageSpeedScore={pageSpeedScore}
        websiteQualityScore={websiteQualityScore}
        trustSecurityScore={trustSecurityScore}
        reportId={reportIdFromUrl}
        style={{ backgroundColor: "#ffffff" }}
      />
    );
    return root;
  };

  const generate = async () => {
    if (isGenerating) return;
    setIsGenerating(true);
    const width = 1280;
    const height = 1280;
    let host: HTMLElement | null = null;
    let root: ReturnType<typeof createRoot> | null = null;
    try {
      host = createOffscreenContainer(width, height);
      root = renderCardInto(host, width, height);

      await new Promise((r) => setTimeout(r, 220));
      try {
        // Ensure web fonts are loaded (Firefox fix). The &apos;fonts&apos; API is not in TS lib DOM types in all setups.
        const anyDocument = document as unknown as {
          fonts?: { ready?: Promise<void> };
        };
        if (anyDocument.fonts?.ready) {
          await anyDocument.fonts.ready;
        }
      } catch {
        // ignore
      }

      const target = host.firstElementChild as HTMLElement | null;
      const nodeToCapture = target ?? host;
      const dataUrl = await htmlToImage.toPng(nodeToCapture, {
        pixelRatio: 1,
        cacheBust: true,
        backgroundColor: "#000000",
        // Avoid embedding fonts to prevent Firefox &apos;font is undefined&apos; errors
        skipFonts: true,
      });

      const link = document.createElement("a");
      let filename = "share.png";
      try {
        if (url) {
          const parsed = new URL(url);
          filename = `${parsed.hostname}.png`;
        }
      } catch {
        if (url) {
          const hostGuess = url.replace(/^https?:\/\//, "").split("/")[0];
          filename = `${hostGuess}.png`;
        }
      }
      link.download = filename;
      link.href = dataUrl;
      link.click();

      // After download, open Twitter with prefilled tweet
      try {
        const hostname = (() => {
          try {
            if (url) return new URL(url).hostname;
            if (typeof window !== "undefined") return window.location.hostname;
            return "";
          } catch {
            return "";
          }
        })();
        const scoreText =
          typeof score === "number" ? `${score}/100` : "your score";
        const siteUrl = "https://yourwebsitescore.com";
        const text = `My website scored ${scoreText} ${
          hostname ? `(${hostname}) ` : ""
        }🚀\nCheck your score at ${siteUrl}`;
        const intent = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
          text
        )}`;
        window.open(intent, "_blank", "noopener,noreferrer");
      } catch {
        // ignore
      }

      // Track share goal
      try {
        window?.datafast?.("report_shared");
      } catch {
        // ignore tracking errors
      }

      // Fire app event: image_shared (non-blocking)
      try {
        const reportId = reportIdFromUrl;
        void fetch("/api/app-events", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            type: "image_shared",
            reportId,
          }),
        });
      } catch {
        // ignore
      }
    } catch (e) {
      toast.error("Share image generation failed");
      console.error("Share image generation failed", e);
    } finally {
      setIsGenerating(false);
      if (root) root.unmount();
      if (host?.parentNode) host.parentNode.removeChild(host);
    }
  };

  return (
    <div className={className}>
      <Button onClick={generate} disabled={isGenerating}>
        {isGenerating ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Generating...
          </>
        ) : (
          <>
            <Share2 className="mr-2 h-4 w-4" /> Share
          </>
        )}
      </Button>
    </div>
  );
}
